import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { LabelComponent } from '../../form/label/label.component';
import { ButtonComponent } from '../../ui/button/button.component';
import { InputFieldComponent } from '../../form/input/input-field.component';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { PermissionService } from '../../../services/permission.service';
import { TwoFactorPromptComponent } from '../two-factor-prompt/two-factor-prompt.component';
import { EmailOtpPromptComponent } from '../email-otp-prompt/email-otp-prompt.component';

@Component({
  selector: 'app-signin-form',
  imports: [
    CommonModule,
    LabelComponent,
    ButtonComponent,
    InputFieldComponent,
    RouterModule,
    FormsModule,
    TwoFactorPromptComponent,
    EmailOtpPromptComponent,
  ],
  templateUrl: './signin-form.component.html',
  styles: ``
})
export class SigninFormComponent implements OnInit, OnDestroy {

  isSubmitting = false;
  showTwoFactorPrompt = false;
  twoFactorError = '';
  isVerifyingTwoFactor = false;
  showEmailOtpPrompt = false;
  emailOtpError = '';
  isVerifyingEmailOtp = false;
  emailOtpExpiresInSeconds?: number;
  emailOtpResendCooldownSeconds?: number;
  showForgotPassword = false;
  isSubmittingForgotPassword = false;

  /** When both Email OTP and 2FA are on, store OTP after backend returns requires2fa so we send both tokens on 2FA submit. */
  private lastEmailOtpToken: string | null = null;

  email = '';
  password = '';
  forgotPasswordEmail = '';
  emailOtpHint?: string;
  firstLoginInfo = '';
  firstLoginError = '';

  private resendCooldownIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    const emailParam = (this.route.snapshot.queryParamMap.get('email') || '').trim();
    const tokenParam = (this.route.snapshot.queryParamMap.get('firstLoginToken') || '').trim();

    if (emailParam && !this.email) {
      this.email = emailParam;
    }

    if (tokenParam) {
      this.firstLoginInfo = 'First-time login detected. Sign in with your temporary password, then change your password.';
      this.authService.verifyFirstLoginToken(tokenParam).subscribe((valid) => {
        if (!valid) {
          this.firstLoginError = 'This first-time login link is invalid or has expired. Please request a new invitation email.';
          this.firstLoginInfo = '';
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.resendCooldownIntervalId != null) {
      clearInterval(this.resendCooldownIntervalId);
      this.resendCooldownIntervalId = null;
    }
  }

  get emailOtpDisplayTarget(): string | undefined {
    if (this.emailOtpHint?.trim()) {
      return this.emailOtpHint.trim();
    }

    const identifier = this.email.trim();
    // If user typed username (not an email), avoid showing it as if it were an email target.
    if (!identifier || !identifier.includes('@')) {
      return undefined;
    }

    return identifier;
  }



  async onSignIn() {
    if (!this.email || !this.password) {
      this.toastService.error('Please enter both email and password', undefined);
      return;
    }

    this.isSubmitting = true;
    this.twoFactorError = '';

    try {
      // Second-factor requirements come from the /login response itself
      // (requires2fa / requiresEmailOtp), which is only returned once the password
      // has been verified. Never pre-check by username: that leaks which accounts exist.
      const loginResponse = await firstValueFrom(this.authService.login(this.email.trim(), this.password));
      if (loginResponse.mustResetPassword === true || this.authService.isPasswordResetRequired()) {
        this.authService.setPendingResetCredentials(this.email.trim(), this.password);
        await this.router.navigate(['/change-password-required']);
        this.isSubmitting = false;
        return;
      }

      if (loginResponse.forceTwoFactorSetup === true || this.authService.isForceTwoFactorSetup()) {
        const setupToken = loginResponse.requiresTwoFactorSetup?.setupToken?.trim() || '';
        if (!setupToken) {
          this.isSubmitting = false;
          this.authService.clearAuthStorage();
          this.toastService.error('Two-factor setup token is missing. Please sign in again.', undefined, '2FA Setup');
          await this.router.navigate(['/signin']);
          return;
        }

        this.authService.setPendingForceTwoFactorSetupToken(
          setupToken,
          loginResponse.requiresTwoFactorSetup?.expiresInSeconds
        );
        await this.router.navigate(['/two-factor-method'], { replaceUrl: true });
        this.isSubmitting = false;
        return;
      }

      // Load permissions (GET /api/users/me/permissions) so menu and guards have them without calling GET /api/roles
      await firstValueFrom(this.permissionService.loadUserPermissions(true));

      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
      this.router.navigate([returnUrl]);
    } catch (error: unknown) {
      const err = error as Error & {
        requires2fa?: boolean;
        requiresEmailOtp?: boolean;
        requiresTwoFactorSetup?: {
          expiresInSeconds: number;
          required: boolean;
          setupToken: string;
        };
        emailHint?: string;
        passwordChangeRequired?: boolean;
      };
      if (err?.passwordChangeRequired === true) {
        this.authService.setPendingResetCredentials(this.email.trim(), this.password);
        await this.router.navigate(['/change-password-required']);
        this.isSubmitting = false;
        return;
      }
      if (err?.requiresTwoFactorSetup?.required === true) {
        const setupToken = err?.requiresTwoFactorSetup?.setupToken?.trim() || '';
        if (!setupToken) {
          this.isSubmitting = false;
          this.authService.clearAuthStorage();
          this.toastService.error('Two-factor setup token is missing. Please sign in again.', undefined, '2FA Setup');
          await this.router.navigate(['/signin']);
          return;
        }

        this.authService.setPendingForceTwoFactorSetupToken(setupToken, err?.requiresTwoFactorSetup?.expiresInSeconds);
        await this.router.navigate(['/two-factor-method'], { replaceUrl: true });
        this.isSubmitting = false;
        return;
      }
      if (err?.requires2fa === true) {
        this.showTwoFactorPrompt = true;
      } else if (err?.requiresEmailOtp === true) {
        this.emailOtpHint = (err as any)?.emailHint;
        this.showEmailOtpPrompt = true;
        this.emailOtpError = '';
        // Call send as soon as we show the OTP page so the backend stores the code.
        // We intentionally suppress UI errors for this first automatic send; user can always use Resend.
        this.requestEmailOtp(false);
      }
      this.isSubmitting = false;
    }
  }

  private async requestEmailOtp(showError: boolean = true): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.authService.sendEmailOtp(this.email.trim(), this.password, 'login')
      );
      this.emailOtpExpiresInSeconds = res?.expiresInSeconds;
      if (typeof res?.emailHint === 'string' && res.emailHint.trim()) {
        this.emailOtpHint = res.emailHint.trim();
      } else {
        const hintFromMessage = this.extractEmailHintFromMessage(res?.message);
        if (hintFromMessage) {
          this.emailOtpHint = hintFromMessage;
        }
      }
      // After a successful send, start a short resend cooldown so the UI immediately shows countdown mode.
      this.startEmailOtpResendCooldown(60);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not send code. You can try "Resend code" in a moment.';
      if (showError) {
        this.emailOtpError = msg;
      }
      const cooldown = this.parseResendCooldown(msg);
      if (cooldown != null && cooldown > 0) {
        this.startEmailOtpResendCooldown(cooldown);
      }
    }
  }

  private startEmailOtpResendCooldown(seconds: number): void {
    const initial = Math.max(0, Math.min(seconds, 120));
    if (initial <= 0) {
      this.emailOtpResendCooldownSeconds = undefined;
      return;
    }

    this.emailOtpResendCooldownSeconds = initial;
    if (this.resendCooldownIntervalId != null) {
      clearInterval(this.resendCooldownIntervalId);
    }
    this.resendCooldownIntervalId = setInterval(() => {
      if (this.emailOtpResendCooldownSeconds == null || this.emailOtpResendCooldownSeconds <= 1) {
        this.emailOtpResendCooldownSeconds = undefined;
        if (this.resendCooldownIntervalId != null) {
          clearInterval(this.resendCooldownIntervalId);
          this.resendCooldownIntervalId = null;
        }
      } else {
        this.emailOtpResendCooldownSeconds = this.emailOtpResendCooldownSeconds - 1;
      }
    }, 1000);
  }

  /** Parse "wait X seconds" from 429 message for resend cooldown. Returns undefined if not rate limited. */
  private parseResendCooldown(message: string): number | undefined {
    if (!message || !/wait|rate|seconds?|another code/i.test(message)) {
      return undefined;
    }
    const match = message.match(/(\d+)\s*seconds?/i);
    return match ? Math.min(parseInt(match[1], 10) || 60, 120) : 60;
  }

  /**
   * Extract masked email from backend message, e.g.:
   */
  private extractEmailHintFromMessage(message?: string): string | undefined {
    if (!message) {
      return undefined;
    }

    const emailLike = message.match(/[A-Za-z0-9._%*+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
    return emailLike?.[0];
  }

  async onEmailOtpSubmit(otp: string): Promise<void> {
    this.isVerifyingEmailOtp = true;
    this.emailOtpError = '';

    try {
      await firstValueFrom(
        this.authService.login(this.email.trim(), this.password, undefined, otp)
      );
      if (this.authService.isPasswordResetRequired()) {
        this.authService.setPendingResetCredentials(this.email.trim(), this.password);
        this.showEmailOtpPrompt = false;
        await this.router.navigate(['/change-password-required']);
        return;
      }
      this.showEmailOtpPrompt = false;
      this.lastEmailOtpToken = null;
      await firstValueFrom(this.permissionService.loadUserPermissions(true));
      const returnUrl = this.route.snapshot.queryParams['returnUrl'];
      const destination = returnUrl
        ? (returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`)
        : '/dashboard';
      await this.router.navigateByUrl(destination, { replaceUrl: true });
    } catch (error: unknown) {
      const err = error as Error & { requires2fa?: boolean; passwordChangeRequired?: boolean };
      if (err?.passwordChangeRequired === true) {
        this.authService.setPendingResetCredentials(this.email.trim(), this.password);
        this.showEmailOtpPrompt = false;
        await this.router.navigate(['/change-password-required']);
        return;
      }
      if (err?.requires2fa === true) {
        this.lastEmailOtpToken = otp;
        this.showEmailOtpPrompt = false;
        this.showTwoFactorPrompt = true;
        this.emailOtpError = '';
        this.twoFactorError = '';
        return;
      }
      this.emailOtpError = error instanceof Error ? error.message : 'Verification failed. Try again.';
    } finally {
      this.isVerifyingEmailOtp = false;
    }
  }

  onEmailOtpBack(): void {
    if (this.resendCooldownIntervalId != null) {
      clearInterval(this.resendCooldownIntervalId);
      this.resendCooldownIntervalId = null;
    }
    this.authService.logout();
    this.lastEmailOtpToken = null;
    this.showEmailOtpPrompt = false;
    this.emailOtpError = '';
    this.emailOtpExpiresInSeconds = undefined;
    this.emailOtpResendCooldownSeconds = undefined;
    this.isVerifyingEmailOtp = false;
  }

  async onEmailOtpResend(): Promise<void> {
    if ((this.emailOtpResendCooldownSeconds ?? 0) > 0) {
      return;
    }
    this.emailOtpError = '';
    await this.requestEmailOtp(true);
  }

  async onTwoFactorSubmit(twoFactorToken: string) {
    this.isVerifyingTwoFactor = true;
    this.twoFactorError = '';

    try {
      const emailOtp = this.lastEmailOtpToken ?? undefined;
      await firstValueFrom(
        this.authService.login(
          this.email.trim(),
          this.password,
          twoFactorToken,
          emailOtp
        )
      );
      if (this.authService.isPasswordResetRequired()) {
        this.authService.setPendingResetCredentials(this.email.trim(), this.password);
        this.lastEmailOtpToken = null;
        this.showTwoFactorPrompt = false;
        await this.router.navigate(['/change-password-required']);
        return;
      }

      this.lastEmailOtpToken = null;
      this.showTwoFactorPrompt = false;

      // Load permissions (GET /api/users/me/permissions) so menu and guards have them
      await firstValueFrom(this.permissionService.loadUserPermissions(true));

      const returnUrl = this.route.snapshot.queryParams['returnUrl'];
      const destination = returnUrl
        ? (returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`)
        : '/dashboard';
      await this.router.navigateByUrl(destination, { replaceUrl: true });
    } catch (error: unknown) {
      const err = error as Error & { passwordChangeRequired?: boolean };
      if (err?.passwordChangeRequired === true) {
        this.authService.setPendingResetCredentials(this.email.trim(), this.password);
        this.lastEmailOtpToken = null;
        this.showTwoFactorPrompt = false;
        await this.router.navigate(['/change-password-required']);
        return;
      }
      this.twoFactorError = error instanceof Error ? error.message : 'Verification failed. Try again.';
    } finally {
      this.isSubmitting = false;
      this.isVerifyingTwoFactor = false;
    }
  }

  onTwoFactorBack() {
    // Clear token/session and return to credentials form
    this.authService.logout();
    this.lastEmailOtpToken = null;
    this.showTwoFactorPrompt = false;
    this.twoFactorError = '';
    this.isVerifyingTwoFactor = false;
  }

  onForgotPasswordClick() {
    this.showForgotPassword = true;
    this.forgotPasswordEmail = this.email; // Pre-fill with email if available
  }

  onForgotPasswordBack() {
    this.showForgotPassword = false;
    this.forgotPasswordEmail = '';
  }

  onForgotPasswordSubmit() {
    // Validation: Empty email
    if (!this.forgotPasswordEmail || !this.forgotPasswordEmail.trim()) {
      this.toastService.error('Please enter your email address', undefined);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.forgotPasswordEmail.trim())) {
      this.toastService.error('Please enter a valid email address', undefined);
      return;
    }

    this.isSubmittingForgotPassword = true;
    this.authService.sendResetEmail(this.forgotPasswordEmail.trim()).subscribe({
      next: (message: string) => {
        this.isSubmittingForgotPassword = false;
        this.toastService.success(message, undefined, 'Password Reset');
        this.onForgotPasswordBack();
      },
      error: (err: Error) => {
        this.isSubmittingForgotPassword = false;
        this.toastService.error(err.message, undefined, 'Password Reset Failed');
      }
    });
  }

}
